// The project-dates mapping the Add-Dates modal used to own (IGNITE-643 relocated that modal into a
// cell-anchored popover; these helpers were already pure and exported, so they move rather than change).
import moment from 'moment';

// A project stores its dates as separate y/m/d numbers; the DateRangePicker works in ISO strings.
export const partsToIso = (year?: number | null, month?: number | null, day?: number | null): string | undefined =>
	year && month && day ? moment({year, month: month - 1, day}).format('YYYY-MM-DD') : undefined;

// The DateRangePicker's onChange receives one interval object of ISO strings, not two positional Dates.
export type DateInterval = {startDate?: string; endDate?: string};

/**
 * Whether the picked range runs backwards - an end strictly before its start.
 *
 * The range picker's calendar popup cannot produce this, but its date segments are editable by
 * keyboard and can: arrowing the start year past the end inverts the range. The design system marks
 * the field aria-invalid when that happens, and styles it, but does not expose that state to the
 * consumer - so nothing can refuse the save unless the comparison is made here.
 *
 * Strict, so a single-day project is valid. A half-open range is incomplete rather than inverted:
 * that is the normal state while filling in a row that is missing one of its dates.
 */
export const isRangeInverted = (interval: DateInterval): boolean =>
	!!interval.startDate && !!interval.endDate && moment(interval.endDate).isBefore(moment(interval.startDate), 'day');

// Maps the picked range onto UpdateProjectInput. Both halves are always sent — cleared ends go as
// explicit nulls so the backend unsets them (mirroring changeStartDate/changeEndDate in
// ProjectSettingsMutations).
export const buildAddDatesInput = (
	projectId: string,
	interval: DateInterval
	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- UpdateProjectInput isn't typed on the JS mutation module
): any => {
	const start = interval.startDate ? moment(interval.startDate) : null;
	const end = interval.endDate ? moment(interval.endDate) : null;
	return {
		project: {id: projectId},
		projectStartYear: start ? start.year() : null,
		projectStartMonth: start ? start.month() + 1 : null,
		projectStartDay: start ? start.date() : null,
		projectEndYear: end ? end.year() : null,
		projectEndMonth: end ? end.month() + 1 : null,
		projectEndDay: end ? end.date() : null,
	};
};
